import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Check, Loader2, Phone, PenLine, MessageSquare } from "lucide-react";
import { Btn } from "@/components/Btn";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";

function OptionCard({ opt, checked, single, onToggle, cashEnabled }) {
  const cash = Number(opt.cash_price) || 0;
  return (
    <button type="button" onClick={onToggle} data-testid={`option-${opt.id}`}
      className={`w-full text-left border-2 border-charcoal rounded-2xl p-4 transition-all ${checked ? "bg-teal/10 shadow-brut -translate-y-0.5" : "bg-white hover:bg-cream"}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 shrink-0 w-6 h-6 border-2 border-charcoal flex items-center justify-center ${single ? "rounded-full" : "rounded-md"} ${checked ? "bg-teal text-cream" : "bg-white"}`}>
          {checked && <Check className="w-4 h-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-heading font-bold text-lg">{opt.title}</span>
            <span className="text-right whitespace-nowrap">
              <span className="font-heading font-bold text-lg text-coral">${opt.price}</span>
              {cashEnabled && cash > 0 && <span className="block font-body text-xs font-bold text-green-700">${cash} cash</span>}
            </span>
          </div>
          {opt.details?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {opt.details.map((d, i) => (
                <li key={i} className="font-body text-sm text-charcoal/80 flex gap-2"><span className="text-teal">•</span><span>{d}</span></li>
              ))}
            </ul>
          )}
          {opt.description && (
            <div className="rich-content mt-2 font-body text-sm text-charcoal/80" dangerouslySetInnerHTML={{ __html: opt.description }} />
          )}
        </div>
      </div>
    </button>
  );
}

export default function QuotePublic() {
  const { id } = useParams();
  const [q, setQ] = useState(undefined);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/quotes/${id}`).then(({ data }) => setQ(data)).catch(() => setQ(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const hasOptions = q?.options?.length > 0;
  const single = q?.selection_mode === "single";
  const mains = useMemo(() => (q?.options || []).filter((o) => !o.addon), [q]);
  const addons = useMemo(() => (q?.options || []).filter((o) => o.addon), [q]);
  const chosenOptions = useMemo(() => (q?.options || []).filter((o) => (q?.selected_option_ids || []).includes(o.id)), [q]);
  const selectedTotal = useMemo(
    () => (q?.options || []).filter((o) => selected.includes(o.id)).reduce((s, o) => s + (Number(o.price) || 0), 0),
    [q, selected]
  );
  const cashEnabled = q?.cash_enabled;
  const optCash = (o) => (Number(o.cash_price) > 0 ? Number(o.cash_price) : Number(o.price) || 0);
  const selectedCashTotal = useMemo(
    () => (q?.options || []).filter((o) => selected.includes(o.id)).reduce((s, o) => s + optCash(o), 0),
    [q, selected]
  );
  const chosenCashTotal = useMemo(() => chosenOptions.reduce((s, o) => s + optCash(o), 0), [chosenOptions]);
  const fixedCash = Number(q?.cash_price) || 0;

  const toggleOption = (opt) => {
    setSelected((prev) => {
      const isSel = prev.includes(opt.id);
      if (single && !opt.addon) {
        const keepAddons = prev.filter((oid) => (q.options.find((o) => o.id === oid) || {}).addon);
        return isSel ? keepAddons : [...keepAddons, opt.id];
      }
      return isSel ? prev.filter((oid) => oid !== opt.id) : [...prev, opt.id];
    });
  };

  const sign = async () => {
    if (!name.trim()) { toast.error("Please type your name to sign."); return; }
    if (hasOptions && !selected.length) { toast.error("Please choose at least one option first."); return; }
    setBusy(true);
    try {
      await api.post(`/public/quotes/${id}/sign`, { signer_name: name.trim(), signature: name.trim(), selected_option_ids: selected, customer_message: message.trim() });
      toast.success("Signed! Thank you. We will be in touch to schedule.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign. Please call us at 843-518-0021."); } finally { setBusy(false); }
  };

  if (q === undefined) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>;
  if (q === null) return <div className="min-h-screen bg-cream flex items-center justify-center px-4 text-center"><div><h1 className="font-heading font-bold text-3xl">Quote not found</h1><p className="font-body text-charcoal/60 mt-2">Please check your link or call us at {BRAND.phoneDisplay}.</p></div></div>;

  const signed = q.status === "signed";
  return (
    <div className="min-h-screen bg-cream py-8 md:py-14 px-4">
      <Helmet><title>Your Skedaddle Quote</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto max-w-2xl">
        <div className="bg-white border-2 border-charcoal rounded-[2rem] shadow-brutlg overflow-hidden" data-testid="public-quote">
          <div className="bg-teal text-cream p-6 md:p-8 border-b-2 border-charcoal">
            <div className="font-heading font-bold text-2xl">Skedaddle Junk Removal</div>
            <div className="font-body text-cream/80 text-sm mt-1">Your quote · {BRAND.phoneDisplay} · {BRAND.hours}</div>
          </div>
          <div className="p-6 md:p-8">
            <p className="font-body text-charcoal/70">Hi {q.customer_name || "there"}, here is your quote!</p>
            {q.job_description && <div className="mt-4 bg-cream border-2 border-charcoal rounded-2xl p-4"><div className="font-heading font-bold text-sm mb-1">The job</div><div className="rich-content font-body text-charcoal/80 text-sm" dangerouslySetInnerHTML={{ __html: q.job_description }} /></div>}

            {/* ---- OPTIONS QUOTE ---- */}
            {hasOptions && !signed && (
              <div className="mt-6" data-testid="options-picker">
                {mains.length > 0 && (
                  <div className="mb-5">
                    <div className="font-heading font-bold text-lg mb-1">{single ? "Choose your option" : "Choose your option(s)"}</div>
                    <p className="font-body text-charcoal/60 text-sm mb-3">{single ? "Pick the one that fits best." : "Pick as many as you'd like, and the total adds up below."}</p>
                    <div className="space-y-3">
                      {mains.map((o) => <OptionCard key={o.id} opt={o} single={single} checked={selected.includes(o.id)} onToggle={() => toggleOption(o)} cashEnabled={cashEnabled} />)}
                    </div>
                  </div>
                )}
                {addons.length > 0 && (
                  <div className="mb-5">
                    <div className="font-heading font-bold text-lg mb-1">Add-ons <span className="font-body text-sm text-charcoal/50">(optional)</span></div>
                    <div className="space-y-3">
                      {addons.map((o) => <OptionCard key={o.id} opt={o} single={false} checked={selected.includes(o.id)} onToggle={() => toggleOption(o)} cashEnabled={cashEnabled} />)}
                    </div>
                  </div>
                )}
                <div className="border-t-2 border-charcoal mt-2 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold text-2xl">Your total</span>
                    <span className="font-heading font-bold text-2xl text-coral" data-testid="options-total">${selectedTotal}</span>
                  </div>
                  {cashEnabled && selectedCashTotal < selectedTotal && selected.length > 0 && (
                    <div className="mt-2 flex items-center justify-between bg-green-50 border-2 border-green-600 rounded-xl px-4 py-2" data-testid="options-cash-total">
                      <span className="font-heading font-bold text-green-800">Pay with cash <span className="font-body text-xs font-bold">(save ${selectedTotal - selectedCashTotal})</span></span>
                      <span className="font-heading font-bold text-xl text-green-800">${selectedCashTotal}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ---- SIGNED OPTIONS: full chosen detail ---- */}
            {signed && hasOptions && chosenOptions.length > 0 && (
              <div className="mt-6" data-testid="chosen-detail">
                <div className="font-heading font-bold text-lg mb-2">What you chose</div>
                <div className="space-y-3">
                  {chosenOptions.map((o) => (
                    <div key={o.id} className="border-2 border-charcoal rounded-2xl p-4 bg-teal/5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-heading font-bold text-lg">{o.title}{o.addon ? " (add-on)" : ""}</span>
                        <span className="text-right whitespace-nowrap">
                          <span className="font-heading font-bold text-lg text-coral">${o.price}</span>
                          {cashEnabled && Number(o.cash_price) > 0 && <span className="block font-body text-xs font-bold text-green-700">${o.cash_price} cash</span>}
                        </span>
                      </div>
                      {o.description && <div className="rich-content mt-2 font-body text-sm text-charcoal/80" dangerouslySetInnerHTML={{ __html: o.description }} />}
                      {(!o.description && o.details?.length > 0) && (
                        <ul className="mt-2 space-y-1">{o.details.map((d, i) => <li key={i} className="font-body text-sm text-charcoal/80 flex gap-2"><span className="text-teal">•</span><span>{d}</span></li>)}</ul>
                      )}
                    </div>
                  ))}
                </div>
                <div className="border-t-2 border-charcoal mt-2 pt-4">
                  <div className="flex items-center justify-between"><span className="font-heading font-bold text-2xl">Total</span><span className="font-heading font-bold text-2xl text-coral" data-testid="quote-total">${q.total}</span></div>
                  {cashEnabled && chosenCashTotal < q.total && (
                    <div className="mt-2 flex items-center justify-between bg-green-50 border-2 border-green-600 rounded-xl px-4 py-2">
                      <span className="font-heading font-bold text-green-800">Cash price <span className="font-body text-xs font-bold">(save ${q.total - chosenCashTotal})</span></span>
                      <span className="font-heading font-bold text-xl text-green-800">${chosenCashTotal}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ---- FIXED QUOTE (or signed fixed summary) ---- */}
            {!hasOptions && (
              <div className="mt-6">
                {q.line_items?.map((li, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-charcoal/15 font-body"><span>{li.label}</span><span className="font-semibold">${li.amount}</span></div>
                ))}
                <div className="flex items-center justify-between py-4 mt-1"><span className="font-heading font-bold text-2xl">Total</span><span className="font-heading font-bold text-2xl text-coral" data-testid="quote-total">${q.total}</span></div>
                {cashEnabled && fixedCash > 0 && (
                  <div className="flex items-center justify-between bg-green-50 border-2 border-green-600 rounded-xl px-4 py-3" data-testid="fixed-cash">
                    <span className="font-heading font-bold text-green-800">Pay with cash {q.total > fixedCash && <span className="font-body text-xs font-bold">(save ${q.total - fixedCash})</span>}</span>
                    <span className="font-heading font-bold text-2xl text-green-800">${fixedCash}</span>
                  </div>
                )}
              </div>
            )}

            {signed ? (
              <div className="mt-4 bg-teal/10 border-2 border-teal rounded-2xl p-5 text-center" data-testid="quote-signed">
                <div className="w-12 h-12 mx-auto rounded-full bg-teal text-cream flex items-center justify-center"><Check className="w-6 h-6" /></div>
                <p className="mt-3 font-heading font-bold text-lg">Signed by {q.signer_name}</p>
                {q.signed_at && <p className="font-body text-charcoal/50 text-xs mt-0.5">{new Date(q.signed_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>}
                {q.customer_message && <p className="font-body text-charcoal/70 text-sm mt-2 italic">"{q.customer_message}"</p>}
                <p className="font-body text-charcoal/60 text-sm mt-1">We will reach out to schedule. Thank you!</p>
              </div>
            ) : (
              <div className="mt-6 bg-cream border-2 border-charcoal rounded-2xl p-5">
                <div className="font-heading font-bold text-lg flex items-center gap-2"><PenLine className="w-5 h-5" /> Approve and sign</div>
                <p className="font-body text-charcoal/60 text-sm mt-1 mb-3">Type your full name to accept{hasOptions ? " your selection" : " this quote"}.</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="sign-name"
                  className="w-full bg-white border-2 border-charcoal rounded-xl px-4 py-3 font-body focus:outline-none focus:ring-4 focus:ring-sunny/50" />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Any questions or notes for us? (optional)" data-testid="sign-message"
                  className="w-full mt-3 bg-white border-2 border-charcoal rounded-xl px-4 py-3 font-body min-h-20 focus:outline-none focus:ring-4 focus:ring-sunny/50" />
                <Btn variant="primary" className="w-full mt-4" onClick={sign} disabled={busy} data-testid="sign-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign and accept"}</Btn>
              </div>
            )}

            <div className="mt-8 pt-6 border-t-2 border-charcoal/15 text-center">
              <div className="font-heading font-bold text-lg">Questions?</div>
              <p className="font-body text-charcoal/60 text-sm mt-1 mb-4">We're happy to help, so reach out anytime.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={BRAND.smsPhotoHref} data-testid="quote-text-btn"
                  className="inline-flex items-center justify-center gap-2 bg-teal text-cream border-2 border-charcoal rounded-full px-6 py-3 font-heading font-bold shadow-brutsm hover:bg-teal/90 transition-colors"><MessageSquare className="w-5 h-5" /> Text us</a>
                <a href={BRAND.phoneHref} data-testid="quote-call-btn"
                  className="inline-flex items-center justify-center gap-2 bg-white text-charcoal border-2 border-charcoal rounded-full px-6 py-3 font-heading font-bold shadow-brutsm hover:bg-cream transition-colors"><Phone className="w-5 h-5" /> Call us</a>
              </div>
              <p className="font-body text-charcoal/40 text-xs mt-3">{BRAND.phoneDisplay}</p>
            </div>
          </div>
        </div>
        <p className="text-center font-body text-charcoal/40 text-xs mt-4">One call and it skedaddles!</p>
      </div>
    </div>
  );
}
